/**
 * Ticket Lifecycle v2 — Slice 3
 *
 * Read + mutation hook pair for the FOH/BOH custody axis.
 *
 * - `useTicketCustody(ticketId)` — reads the current custody row for a
 *   ticket. Returns `null` when no row exists yet; consumers must treat
 *   "no row" as the default starting position (BOH / with_tech).
 * - `useTicketCustodyMutation()` — calls the canonical edge function
 *   `transition-ticket-custody`, which is the only client-reachable
 *   write path (DB trigger rejects direct writes outside the RPC).
 *
 * The hook does not import from any scheduling, timer, parking, rollover,
 * bay, or invariants module — per the runtime-boundary guard in
 * `tests/unit/server/custodyEnumDriftDetector.test.ts`.
 *
 * See: docs/lifecycle/TICKET_LIFECYCLE_V2_SPEC.md §2.3, §4.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import type {
  CustodyEventKind,
  CustodyOwner,
  CustodyStatus,
} from '@/lib/tickets/lifecycle/types';
import {
  useRecordAdvisorAction,
  advisorActionForCustodyEvent,
} from '@/hooks/tickets/useRecordAdvisorAction';
import {
  fetchRemainingApprovedWorkForTicket,
  unavailableRemainingApprovedWorkMetadata,
} from '@/lib/tickets/lifecycle/fetchRemainingApprovedWorkForTicket';
import { logTicketEventToOcis } from '@/utils/ocisIngest';
import { buildCustodyOcisEvent } from '@/lib/tickets/lifecycle/custodyOcisEvents';

export interface TicketCustodyRow {
  ticket_id: string;
  shop_id: string;
  custody_owner: CustodyOwner;
  custody_status: CustodyStatus;
  holder_user_id: string | null;
  entered_at: string;
  entered_by: string | null;
  last_event_id: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Default custody state shown when no row exists for a ticket yet.
 * Mirrors the lazy-init values in
 * `_apply_custody_transition` (migration 20260521120000).
 */
export const DEFAULT_CUSTODY: Pick<
  TicketCustodyRow,
  'custody_owner' | 'custody_status'
> = {
  custody_owner: 'BOH',
  custody_status: 'with_tech',
};

export function useTicketCustody(ticketId?: string | null) {
  return useQuery({
    queryKey: ['ticket-custody', ticketId],
    enabled: !!ticketId,
    // Slice 3.6: don't auto-retry — if the table is missing on a dev
    // project, a noisy retry loop hides the real error. Log once and
    // let consumers fall back to DEFAULT_CUSTODY.
    retry: false,
    queryFn: async (): Promise<TicketCustodyRow | null> => {
      const { data, error } = await (supabase as any)
        .from('ticket_custody')
        .select('*')
        .eq('ticket_id', ticketId)
        .maybeSingle();
      if (error) {
        console.error(
          '[custody] failed to read ticket_custody — is the slice-2 ' +
            'migration applied to this Supabase project?',
          error,
        );
        throw error;
      }
      return (data ?? null) as TicketCustodyRow | null;
    },
  });
}

export interface TransitionArgs {
  ticketId: string;
  eventKind: CustodyEventKind;
  toUserId?: string | null;
  reason?: string | null;
  metadata?: Record<string, unknown>;
}

interface TransitionResponse {
  ok: true;
  custody_owner: CustodyOwner;
  custody_status: CustodyStatus;
  last_event_id: string | null;
}

async function invokeTransition(args: TransitionArgs): Promise<TransitionResponse> {
  const { data, error } = await supabase.functions.invoke('transition-ticket-custody', {
    body: {
      ticket_id: args.ticketId,
      event_kind: args.eventKind,
      to_user_id: args.toUserId ?? null,
      reason: args.reason ?? null,
      metadata: args.metadata ?? {},
    },
  });
  // Slice 3.6: defensive error normalization. Surfaces three failure modes
  // explicitly so deployment gaps stop looking like a dead click:
  //   1. `error` set by supabase-js (network, CORS, 4xx/5xx with parseable body)
  //   2. `data.error` returned by the edge function itself (role denied, RPC reject)
  //   3. `data` is null with no error (edge function not deployed at all)
  if (error) {
    const message =
      (data as { error?: string; message?: string } | null)?.error ||
      (data as { error?: string; message?: string } | null)?.message ||
      error.message ||
      'Custody transition failed (network/auth error)';
    console.error('[custody] supabase.functions.invoke error', error, data);
    throw new Error(message);
  }
  if (!data) {
    console.error(
      '[custody] empty response from transition-ticket-custody — ' +
        'is the slice-2 edge function deployed to this project?',
    );
    throw new Error(
      'transition-ticket-custody returned no data. The edge function may not be deployed to this Supabase project.',
    );
  }
  const payload = data as Partial<TransitionResponse> & { error?: string };
  if (payload.error) {
    throw new Error(payload.error);
  }
  return data as TransitionResponse;
}

/**
 * The follow-up of a COMMITTED custody transition: cache refresh, OCIS
 * timeline event, advisor-action emission and (optionally) the success toast.
 *
 * Shared by the edge-function path (useTicketCustodyMutation) and by server
 * acts that perform the transition themselves in their own transaction
 * (Stage E finish_my_work) — so a server-side handoff is followed up exactly
 * like a client-requested one. Call it ONLY with a committed custody event.
 */
export function useCustodySuccessEffects() {
  const qc = useQueryClient();
  // Slice 4B: fire-and-forget advisor action emission. The recorder
  // is a no-op when `advisorTimingAnalytics` flag is off; when on it
  // dispatches to the `record-advisor-action` edge function and logs
  // failures to console without surfacing toasts. Custody success is
  // never gated on advisor-action emission.
  const recordAdvisorAction = useRecordAdvisorAction();
  return (vars: TransitionArgs, lastEventId: string | null, opts: { toast?: boolean } = {}) => {
      const data = { last_event_id: lastEventId };
      qc.invalidateQueries({ queryKey: ['ticket-custody', vars.ticketId] });
      qc.invalidateQueries({ queryKey: ['ticket-custody-events', vars.ticketId] });
      // The full history (Command Center handoff card + Custody Handoffs
      // panel) is a separate key; it was never refreshed by a transition.
      qc.invalidateQueries({ queryKey: ['ticket-custody-history', vars.ticketId] });
      // K19-4 (H6): also refresh the FOH custody queue on the ACTING device.
      // The transition itself is correct (e.g. boh_accept moves
      // pending_tech_accept → with_tech, which leaves FOH_QUEUE_STATUSES), but
      // previously only the (dead, unpublished) realtime hook invalidated
      // `foh-custody-queue` — so after accepting a BOH return the acting
      // device's queue and "BOH · Pending Tech" indicator stayed stale until a
      // manual refresh. Prefix-key invalidation refreshes the queue for the
      // active shop without threading shop_id through TransitionArgs.
      qc.invalidateQueries({ queryKey: ['foh-custody-queue'] });

      // Batch 1 (FOH timer handoff): emit custody event to OCIS
      // timeline so reviewers see FOH handoff activity. Fire-and-forget
      // — never gates the custody mutation result, errors only log.
      // Resolves shop_id via a lightweight ticket lookup so callers do
      // not have to thread shop_id through TransitionArgs.
      void (async () => {
        try {
          const plan = buildCustodyOcisEvent({
            eventKind: vars.eventKind,
            reason: vars.reason ?? null,
            metadata: vars.metadata ?? null,
            custodyEventId: data?.last_event_id ?? null,
          });
          if (!plan) return;
          const { data: ticketRow, error: ticketErr } = await (supabase as any)
            .from('tickets')
            .select('shop_id')
            .eq('id', vars.ticketId)
            .maybeSingle();
          if (ticketErr || !ticketRow?.shop_id) {
            console.warn(
              '[custody-ocis] shop_id lookup failed; skipping timeline emit',
              ticketErr,
            );
            return;
          }
          const res = await logTicketEventToOcis({
            ticketId: vars.ticketId,
            shopId: ticketRow.shop_id as string,
            eventType: plan.eventType,
            content: plan.content,
            payload: plan.payload,
          });
          if (!res?.success) {
            console.warn(
              '[custody-ocis] timeline emit failed (non-blocking)',
              res?.error,
            );
          }
        } catch (e) {
          console.warn('[custody-ocis] unexpected error (non-blocking)', e);
        }
      })();

      // Slice 4D.1 follow-up: the shadcn `toast` helper auto-dispatches
      // a `toast-notification` window event that NotificationPanel
      // catches and adds to the bell's in-memory list. For custody
      // transitions, the persistent DB row (slice 4D) is the canonical
      // record — bridging this generic toast as well masks the real
      // notification (no target_route, no useful title). `skipPersist`
      // suppresses the bridge for this transient success cue. The
      // floating toast UI still appears as before. Error path
      // intentionally keeps the bridge: a failure is worth persisting
      // for ops triage.
      if (opts.toast !== false) {
        toast({ title: custodyToastTitleForEventKind(vars.eventKind), skipPersist: true });
      }

      // Slice 4B: emit advisor action for the captured custody
      // transitions. Detached promise — never awaited, never throws
      // to the caller. See spec §4 + §6.1.
      //
      // Slice 4B (initial):
      //   - foh_accept   → ticket_accepted
      //   - kick_to_boh  → kicked_back_to_tech
      //
      // FOH Timer Handoff Batch 1:
      //   - kick_to_foh  → ticket_kicked_to_foh (symmetric initiation)
      //
      // Slice 5D-2: enrich `foh_accept` (→ `ticket_accepted`) metadata
      // with the remaining-approved-work snapshot. This is the input
      // the future diag_plus_work overlap math needs (slice 5D-3+).
      // Other custody transitions retain the original two-key metadata
      // exactly. The whole enrichment runs inside the detached async
      // IIFE so the mutation's `onSuccess` resolves immediately and
      // the optional ticket_jobs fetch never blocks the UI.
      //
      // FOH Timer Handoff Batch 1: also merge the caller-supplied
      // handoff metadata (trigger / trigger_reason / etc.) so the
      // advisor_action_events row is queryable by the same keys as the
      // ticket_custody_events row that triggered it.
      const advisorKind = advisorActionForCustodyEvent(vars.eventKind);
      if (advisorKind) {
        void (async () => {
          let metadata: Record<string, unknown> = {
            source: 'custody_transition',
            custody_event: vars.eventKind,
            ...(vars.metadata ?? {}),
          };
          if (vars.eventKind === 'foh_accept') {
            let captured;
            try {
              captured = await fetchRemainingApprovedWorkForTicket(
                supabase as unknown as Parameters<
                  typeof fetchRemainingApprovedWorkForTicket
                >[0],
                vars.ticketId,
              );
            } catch (e) {
              console.error(
                '[custody] remaining-approved-work capture failed',
                e,
              );
              captured = unavailableRemainingApprovedWorkMetadata();
            }
            metadata = {
              ...metadata,
              ...captured,
              remaining_approved_work_captured_at: new Date().toISOString(),
            };
          }
          try {
            await recordAdvisorAction({
              ticketId: vars.ticketId,
              actionKind: advisorKind,
              metadata,
            });
          } catch (e) {
            console.error('[advisor-action] post-custody emit failed', e);
          }
        })();
      }
  };
}

export function useTicketCustodyMutation() {
  const afterSuccess = useCustodySuccessEffects();
  return useMutation({
    mutationFn: invokeTransition,
    onSuccess: (data, vars) => afterSuccess(vars, data?.last_event_id ?? null),
    onError: (e: Error) => {
      toast({
        title: 'Custody transition rejected',
        description: e.message,
        variant: 'destructive',
      });
    },
  });
}

// ─── Pure mapping helpers (testable without DOM / Supabase) ────────────

export interface CustodyAction {
  kind: CustodyEventKind;
  label: string;
  /** When true, the action is a kick handoff and may prompt for a reason. */
  promptReason: boolean;
}

/**
 * Maps the current custody_status to the single "primary" forward action
 * exposed in the kebab menu. Returns `null` when no primary action is
 * available for the current state. Pure: no side effects, no I/O.
 */
export function primaryCustodyAction(
  status: CustodyStatus,
  opts?: {
    /**
     * Core-100 R2 (CORE-AK, owner direction 6): whether the viewer acts as front office (service
     * advisor / manager / owner, multi-role aware). FOH acceptance is a front-office act: when this
     * is explicitly `false`, a pending FOH acceptance offers NO action. Omitted keeps the old
     * behaviour for callers that gate elsewhere.
     */
    actsAsFrontOffice?: boolean;
  },
): CustodyAction | null {
  switch (status) {
    case 'with_tech':
      return { kind: 'kick_to_foh', label: 'Kick to Advisor', promptReason: true };
    case 'pending_advisor_accept':
      if (opts?.actsAsFrontOffice === false) return null;
      return { kind: 'foh_accept', label: 'Accept Ticket', promptReason: false };
    case 'with_advisor':
      return { kind: 'kick_to_boh', label: 'Kick Back to Tech', promptReason: true };
    case 'pending_tech_accept':
      return { kind: 'boh_accept', label: 'Accept Back', promptReason: false };
    case 'released_to_customer':
      // Oct 20 (FD-05): the customer has the vehicle; custody does not move after pickup.
      return null;
  }
}

/**
 * Toast-title text for a custody transition, specialized by event kind.
 *
 * Replaces the prior generic "Custody updated" message. The four
 * supported event kinds map to the four directional moves the operator
 * just performed; anything else falls back to the legacy string so we
 * don't accidentally lose the success cue on a future event kind.
 *
 * This is shop-global toast text (everyone watching the same shop sees
 * the same toast). It is intentionally NOT a tech-only notification —
 * the codebase has no role-aware user-facing notification path yet.
 * When that path lands, the assigned tech can be paged separately; this
 * helper covers the immediate "the operator who did the action sees a
 * meaningful confirmation" gap.
 *
 * Pure: same input ⇒ same output.
 */
export function custodyToastTitleForEventKind(eventKind: CustodyEventKind): string {
  switch (eventKind) {
    case 'kick_to_foh':
      return 'Ticket sent to advisor';
    case 'foh_accept':
      return 'Ticket accepted by advisor';
    case 'kick_to_boh':
      return 'Ticket sent back to technician';
    case 'boh_accept':
      return 'Ticket accepted back to BOH';
    default:
      return 'Custody updated';
  }
}

/**
 * The custody rule for a successful timer start/resume lives in a pure module
 * (`@/lib/tickets/lifecycle/custodyOnWork`) so every surface — and the
 * real-stack workflow gate — runs the same rule. Re-exported for callers.
 */
export { nextCustodyEventOnTimerStart } from '@/lib/tickets/lifecycle/custodyOnWork';

/**
 * Humanised label for the custody_status enum, suitable for badges.
 * Pure: same input ⇒ same output.
 */
export function custodyStatusLabel(status: CustodyStatus): string {
  switch (status) {
    case 'with_tech':
      return 'With Tech';
    case 'pending_advisor_accept':
      return 'Pending Advisor';
    case 'with_advisor':
      return 'With Advisor';
    case 'pending_tech_accept':
      return 'Pending Tech';
    case 'released_to_customer':
      return 'Picked Up';
  }
}
